import el from "@cypherpotato/el";
import page from "page";
import { api } from "../../api";
import { BigTextPreview } from "../../components/big-text-preview";
import { Button } from "../../components/button";
import { CodeEditor } from "../../components/code-editor";
import { ColorPickerControl } from "../../components/color-picker";
import { CommandBar } from "../../components/command-bar";
import { Dashboard, DashboardTitle } from "../../components/dashboard";
import { DataPickerControl, MultiDataPickerControl } from "../../components/data-picker";
import { Dropdown, DropdownDivider, DropdownItem } from "../../components/dropdown";
import { Editor, EditorDescription, EditorTitle } from "../../components/editor";
import { NumberInput, TextInput } from "../../components/form";
import { GatewayPickerControl } from "../../components/gateway-picker";
import { Icon } from "../../components/icon";
import { ImageControl } from "../../components/image";
import { Section } from "../../components/layout";
import { Link } from "../../components/link";
import { SuggestionButtonEditor } from "../../components/suggestion-button-editor";
import { TabControl } from "../../components/tabcontrol";
import { CheckWrapper, DashboardTextHeading, Heading, MutedText, Paragraph } from "../../components/text";
import { displayAlert, displayFormControl } from "../../ui/alert";
import { formatNumber } from "../../ui/format";
import { runOperation } from "../../ui/operation";
import { getUniqueColor } from "../../ui/unique-color";
import { ellipsis, valueFrom } from "../../util";

const INPUT_MODES = [
    { value: "Image", text: "Image" },
    { value: "Audio", text: "Audio" },
    { value: "Document", text: "Document" }
];

const AUDIO_SOURCES = [
    { value: "None", text: "None" },
    { value: "Gpt4oTts", text: "GPT-4o TTS" },
    { value: "ElevenMultilingualV2", text: "Eleven Multilingual V2" },
    { value: "ElevenV3", text: "Eleven V3" }
];

const LANGUAGES = [
    { value: null, text: "Auto detect" },
    { value: "pt-BR", text: "Português Brasileiro" },
    { value: "en-US", text: "English (US)" }
];

function enumValue(value, fallback) {
    return Array.isArray(value) ? value[0] : (value ?? fallback);
}

export default function (ctx) {

    const chatClientId = ctx.params.id;
    const isCreatingChatClient = chatClientId == "create";
    const chatClient = el.state.create(null);

    function updateView() {
        runOperation("Fetching chat client...", async () => {
            const response = await api.getJson(`/api/v1/web-chat-client/${chatClientId}`);
            chatClient.value = response.data;
        });
    }

    if (!isCreatingChatClient) {
        updateView();
    }

    function handleSave() {
        const data = {
            name: valueFrom("chatClientName"),
            aiGatewayId: valueFrom("chatClientAssociatedGateway"),
            clientParameters: {
                languageCode: valueFrom("chatClientLanguage"),
                primaryColor: valueFrom("chatClientPrimaryColor"),
                pageTitle: valueFrom("chatClientPageTitle"),
                helloLabel: valueFrom("chatClientHelloLabel")?.nullWhenEmpty(),
                helloSubLabel: valueFrom("chatClientHelloSubLabel")?.nullWhenEmpty(),
                logoImageUrl: valueFrom("chatClientLogoImageSource")?.nullWhenEmpty(),
                suggestionButtons: valueFrom("chatClientSuggestionButtons") || [],
                textAreaPlaceholder: valueFrom("chatClientPlaceholderLabel")?.nullWhenEmpty(),
                customStyles: valueFrom("chatClientCustomCss")?.nullWhenEmpty(),
                customScripts: valueFrom("chatClientCustomJavaScript")?.nullWhenEmpty(),
                inputModes: valueFrom("chatClientInputModes") || [],
                debug: valueFrom("chatClientEnableDebug"),
                showToolCalls: valueFrom("chatClientShowToolCalls"),
                allowedFrameOrigins: valueFrom("chatClientAllowedFrameOrigins") || [],
                audioSynthesisSource: valueFrom("chatClientAudioSynthesisSource"),
                audioSynthesisVoice: valueFrom("chatClientAudioSynthesisVoice")?.nullWhenEmpty(),
                audioSynthesisInstruction: valueFrom("chatClientAudioSynthesisInstruction")?.nullWhenEmpty(),
                summarizeTextBeforeAudioSynthesis: valueFrom("chatClientAudioSynthesisSummarize"),
                uploadUnsupportedFiles: valueFrom("chatClientUploadUnsupportedFiles")
            },
            limitingParameters: {
                maxMessages: valueFrom("chatClientMaxMessages")
            }
        };

        runOperation("Saving chat client...", async () => {
            if (isCreatingChatClient) {
                const response = await api.postJson("/api/v1/web-chat-client", data);
                page(`/dashboard/chat-clients/${response.data.id}`);
            } else {
                await api.putJson(`/api/v1/web-chat-client/${chatClientId}`, data);
                updateView();
            }
        });
    }

    return el.state.listening([chatClient], () =>
        Dashboard(
            Section(
                Link(el.fragment(Icon("arrow-left-line"), "Return back"), null, {
                    onClick() {
                        history.go(-1);
                        page();
                    }
                })
            ),
            DashboardTitle(isCreatingChatClient ? "New chat client" : DashboardTextHeading(chatClient.value?.name, "Chat client details")),
            Section(
                CommandBar(
                    Button(
                        Icon("save-line"),
                        isCreatingChatClient ? "Create chat client" : "Save changes",
                        {
                            onClick: handleSave
                        }
                    ),
                    !isCreatingChatClient && Dropdown("Manage",
                        DropdownItem("Sessions", Icon("discuss-line"), {
                            onClick() {
                                page(`/dashboard/chat-clients/${chatClientId}/sessions`);
                            }
                        }),
                        DropdownItem("New session", Icon("chat-new-line"), {
                            onClick() {
                                page(`/dashboard/chat-clients/${chatClientId}/sessions/create`);
                            }
                        }),
                        DropdownDivider(),
                        DropdownItem("Delete", Icon("delete-bin-7-line"), {
                            onClick() {
                                displayAlert({
                                    title: "Delete chat client",
                                    message: "Are you sure you want to delete this chat client and its sessions? This action cannot be undone.",
                                    type: "alert-warning",
                                    buttons: [
                                        {
                                            label: "Delete chat client",
                                            onClick(event) {
                                                runOperation("Deleting chat client...", async () => {
                                                    await api.deleteJson(`/api/v1/web-chat-client/${chatClientId}`);
                                                    page("/dashboard/chat-clients");
                                                });
                                                event.target.closest("dialog").dismiss();
                                            }
                                        },
                                        {
                                            label: "Cancel",
                                            onClick(event) {
                                                event.target.closest("dialog").dismiss();
                                            }
                                        }
                                    ]
                                });
                            }
                        })
                    )
                )
            ),
            Section(
                TabControl([
                    {
                        title: "Chat client",
                        icon: Icon("question-answer-line"),
                        contents: el.fragment(
                            Section(
                                Editor(
                                    {
                                        id: "chatClientName",
                                        value: chatClient.value?.name,
                                        renderValue(value) {
                                            return Button(value || "No name");
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: TextInput(null, "Chat client name", { value }),
                                                onConfirm: callback,
                                                validate: [
                                                    [x => x.length > 3, "The chat client name must be at least 3 characters long."]
                                                ]
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Chat client name"),
                                        Paragraph("Friendly name used inside the dashboard.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "chatClientAssociatedGateway",
                                        value: chatClient.value?.aiGateway?.id,
                                        renderValue(value) {
                                            return Button(value || MutedText("None"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: GatewayPickerControl({ value, allowNone: false }),
                                                onConfirm: callback,
                                                dialogSize: "large",
                                                validate: [
                                                    [x => !!x, "Choose an AI gateway."]
                                                ]
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Associated gateway"),
                                        Paragraph("Gateway that will process this chat client's messages.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "chatClientLanguage",
                                        value: chatClient.value?.clientParameters.languageCode || null,
                                        renderValue(value) {
                                            return Button(value || "Auto detect");
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: DataPickerControl({ value, data: LANGUAGES, allowNone: true }),
                                                onConfirm: callback,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("UI language"),
                                        Paragraph("Language used by interface labels.")
                                    )
                                )
                            )
                        )
                    },
                    {
                        title: "Customization",
                        icon: Icon("quill-pen-line"),
                        contents: el.fragment(
                            Section(
                                Editor(
                                    {
                                        id: "chatClientPrimaryColor",
                                        value: chatClient.value?.clientParameters.primaryColor || getUniqueColor(Math.random().toString()),
                                        renderValue(value) {
                                            return ColorPickerControl(value);
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: ColorPickerControl(value, { classList: ["large"] }),
                                                onConfirm: callback,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Primary color"),
                                        Paragraph("Highlight color used by the chat UI.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "chatClientPageTitle",
                                        value: chatClient.value?.clientParameters.pageTitle || "My web chat",
                                        renderValue(value) {
                                            return Button(value || MutedText("None"))
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: TextInput(null, "Page title", { value }),
                                                onConfirm: callback,
                                                validate: [
                                                    [x => x.length > 3, "The page title must be at least 3 characters long."]
                                                ]
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Page title"),
                                        Paragraph("Title shown in the browser tab.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "chatClientLogoImageSource",
                                        value: chatClient.value?.clientParameters.logoImageUrl,
                                        renderValue(value) {
                                            return ImageControl({ value });
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: ImageControl({ value }),
                                                onConfirm: callback,
                                                validate: [],
                                                nullButton: true
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Logo image"),
                                        Paragraph("Optional image displayed in the chat presentation.")
                                    )
                                )
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "chatClientHelloLabel",
                                        value: chatClient.value?.clientParameters.helloLabel,
                                        renderValue(value) {
                                            return Button(ellipsis(value) || MutedText("None"))
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: TextInput(null, "Presentation header", { value }),
                                                onConfirm: callback,
                                                nullButton: true,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(EditorTitle("Presentation title"))
                                ),
                                Editor(
                                    {
                                        id: "chatClientHelloSubLabel",
                                        value: chatClient.value?.clientParameters.helloSubLabel,
                                        renderValue(value) {
                                            return Button(ellipsis(value) || MutedText("None"))
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: TextInput(null, "Presentation subtitle", { value }),
                                                onConfirm: callback,
                                                nullButton: true,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(EditorTitle("Presentation subtitle"))
                                ),
                                Editor(
                                    {
                                        id: "chatClientPlaceholderLabel",
                                        value: chatClient.value?.clientParameters.textAreaPlaceholder,
                                        renderValue(value) {
                                            return Button(value || MutedText("None"))
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: TextInput(null, "Talk with the assistant", { value }),
                                                onConfirm: callback,
                                                nullButton: true,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(EditorTitle("Input area placeholder"))
                                ),
                                Editor(
                                    {
                                        id: "chatClientSuggestionButtons",
                                        value: chatClient.value?.clientParameters.suggestionButtons || [],
                                        renderValue(value) {
                                            return Button(value?.length ? `${formatNumber(value.length)} suggestion(s)` : MutedText("None"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: SuggestionButtonEditor({ value }),
                                                dialogSize: "large",
                                                onConfirm: callback,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(EditorTitle("Suggestion buttons"))
                                )
                            )
                        )
                    },
                    {
                        title: "Behavior",
                        icon: Icon("list-settings-line"),
                        contents: el.fragment(
                            Section(
                                Editor(
                                    {
                                        id: "chatClientMaxMessages",
                                        value: chatClient.value?.limitingParameters.maxMessages ?? 500,
                                        renderValue(value) {
                                            return Button(formatNumber(value));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: NumberInput({ value, min: 1, max: 50000, step: 5 }),
                                                onConfirm: callback,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Maximum messages"),
                                        Paragraph("Maximum number of messages kept in the session context.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "chatClientInputModes",
                                        value: chatClient.value?.clientParameters.inputModes ?? [],
                                        renderValue(value) {
                                            return Button(value?.join(", ").nullWhenEmpty() || MutedText("Only text"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: MultiDataPickerControl({ value, data: INPUT_MODES }),
                                                onConfirm: callback,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Input modes"),
                                        Paragraph("File types accepted by the visual chat client.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "chatClientUploadUnsupportedFiles",
                                        value: chatClient.value?.clientParameters.uploadUnsupportedFiles ?? false,
                                        renderValue(value) {
                                            return CheckWrapper(value);
                                        },
                                        manipulateValue(value, callback) {
                                            callback(!value);
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Upload unsupported files"),
                                        Paragraph("Allow users to upload files that cannot be embedded directly by the model.")
                                    )
                                )
                            )
                        )
                    },
                    {
                        title: "Text-to-speech",
                        icon: Icon("volume-up-line"),
                        contents: el.fragment(
                            Section(
                                Editor(
                                    {
                                        id: "chatClientAudioSynthesisSource",
                                        value: enumValue(chatClient.value?.clientParameters.audioSynthesisSource, "None"),
                                        renderValue(value) {
                                            return Button(value || MutedText("None"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: DataPickerControl({ value, data: AUDIO_SOURCES }),
                                                onConfirm: callback,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Voice synthesis source"),
                                        Paragraph("Provider used to synthesize assistant messages as audio.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "chatClientAudioSynthesisVoice",
                                        value: chatClient.value?.clientParameters.audioSynthesisVoice,
                                        renderValue(value) {
                                            return Button(value || MutedText("Unset"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: TextInput(null, "nova, onyx, ash...", { value }),
                                                onConfirm: callback,
                                                nullButton: true,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(EditorTitle("Synthesis voice"))
                                ),
                                Editor(
                                    {
                                        id: "chatClientAudioSynthesisInstruction",
                                        value: chatClient.value?.clientParameters.audioSynthesisInstruction,
                                        renderValue(value) {
                                            return BigTextPreview(value);
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                dialogSize: "large",
                                                formControlItem: CodeEditor({ value }),
                                                onConfirm: callback,
                                                nullButton: true,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(EditorTitle("Special instructions"))
                                ),
                                Editor(
                                    {
                                        id: "chatClientAudioSynthesisSummarize",
                                        value: chatClient.value?.clientParameters.summarizeTextBeforeAudioSynthesis ?? false,
                                        renderValue(value) {
                                            return CheckWrapper(value);
                                        },
                                        manipulateValue(value, callback) {
                                            callback(!value);
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Summarize before synthesis"),
                                        Paragraph("Shorten generated text before creating audio.")
                                    )
                                )
                            )
                        )
                    },
                    {
                        title: "Advanced",
                        icon: Icon("terminal-box-line"),
                        contents: el.fragment(
                            Section(
                                Editor(
                                    {
                                        id: "chatClientCustomCss",
                                        value: chatClient.value?.clientParameters.customStyles,
                                        renderValue(value) {
                                            return BigTextPreview(value, "css")
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                dialogSize: "large",
                                                formControlItem: CodeEditor({ value, language: "css" }),
                                                onConfirm: callback,
                                                validate: [],
                                                nullButton: true
                                            })
                                        }
                                    },
                                    EditorDescription(EditorTitle("Custom styles"))
                                ),
                                Editor(
                                    {
                                        id: "chatClientCustomJavaScript",
                                        value: chatClient.value?.clientParameters.customScripts,
                                        renderValue(value) {
                                            return BigTextPreview(value, "javascript")
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                dialogSize: "large",
                                                formControlItem: CodeEditor({ value, language: "javascript" }),
                                                onConfirm: callback,
                                                validate: [],
                                                nullButton: true
                                            })
                                        }
                                    },
                                    EditorDescription(EditorTitle("Custom script"))
                                )
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "chatClientEnableDebug",
                                        value: chatClient.value?.clientParameters.debug ?? false,
                                        renderValue(value) {
                                            return CheckWrapper(value, "enabled", "disabled");
                                        },
                                        manipulateValue(value, callback) {
                                            callback(!value);
                                        }
                                    },
                                    EditorDescription(EditorTitle("Debug"))
                                ),
                                Editor(
                                    {
                                        id: "chatClientShowToolCalls",
                                        value: chatClient.value?.clientParameters.showToolCalls ?? true,
                                        renderValue(value) {
                                            return CheckWrapper(value, "visible", "hidden");
                                        },
                                        manipulateValue(value, callback) {
                                            callback(!value);
                                        }
                                    },
                                    EditorDescription(EditorTitle("Tool call visibility"))
                                )
                            )
                        )
                    }
                ])
            )
        )
    )
}
